import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { ShoppingBag, Store, X, ChevronLeft, ChevronRight, FileText, Search, Plus, Minus, Filter, Menu, Star, Trash2, ShoppingBasket, Share2, Check, AlertTriangle, MessageCircle, ArrowLeft } from 'lucide-react';
import { getProducts, getStoreInfo, getCategories } from '../services/api';
import CheckoutModal from '../components/CheckoutModal';
import CartModal from '../components/CartModal';
import CartBar from '../components/CartBar';
import Footer from '../components/Footer';
import ShareModal from '../components/ShareModal';
import { useToast } from '../context/ToastContext';

const Skeleton = ({ className }: { className?: string }) => (
   <div className={`animate-pulse bg-surface-200 rounded-lg ${className}`} />
);

const BackorderModal = ({ isOpen, onClose, onConfirm, primaryColor }: any) => {
   if (!isOpen) return null;
   return (
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
         <div className="bg-white w-full max-w-sm rounded-3xl p-6 shadow-2xl transform transition-all scale-100 border border-gray-100">
            <div className="w-14 h-14 bg-amber-50 rounded-full flex items-center justify-center mb-5 mx-auto border border-amber-100 shadow-sm">
               <AlertTriangle size={28} className="text-amber-500" />
            </div>
            <h3 className="text-xl font-bold text-center text-gray-900 mb-3">Item Sob Encomenda</h3>
            <p className="text-sm text-gray-500 text-center mb-8 leading-relaxed">Este produto não está em estoque imediato e será produzido/encomendado especialmente para você. O prazo de entrega será estendido.</p>
            <div className="flex gap-3">
               <button onClick={onClose} className="flex-1 py-3 bg-gray-50 text-gray-700 font-bold rounded-xl hover:bg-gray-100 transition border border-gray-200 uppercase text-xs tracking-wide">Cancelar</button>
               <button onClick={onConfirm} className="flex-1 py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-black transition shadow-lg shadow-gray-900/20 uppercase text-xs tracking-wide">Confirmar</button>
            </div>
         </div>
      </div>
   );
};

const PublicStore = () => {
   const { tenantId } = useParams();
   const { showToast } = useToast();
   const [searchParams] = useSearchParams();
   const [store, setStore] = useState<any>(null);
   const [products, setProducts] = useState<any[]>([]);
   const [categories, setCategories] = useState<any[]>([]);

   // --- FILTROS ---
   const [selectedCategory, setSelectedCategory] = useState<string>('all');
   const [searchTerm, setSearchTerm] = useState('');
   const [priceRange, setPriceRange] = useState<[number, number]>([0, 10000]);
   const [showFiltersMobile, setShowFiltersMobile] = useState(false);

   // --- MODAL & CART ---
   const [selectedProduct, setSelectedProduct] = useState<any>(null);
   const [isCartOpen, setIsCartOpen] = useState(false);
   const [currentImageIndex, setCurrentImageIndex] = useState(0);
   const [cart, setCart] = useState<{ [key: string]: { qty: number, variations?: any } }>({});
   const [loading, setLoading] = useState(true);

   // --- NOVOS ESTADOS ---
   const [selectedVariations, setSelectedVariations] = useState<{ [productId: string]: { [vName: string]: string } }>({});
   const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);
   const [showBackorderModal, setShowBackorderModal] = useState(false);
   const [pendingAction, setPendingAction] = useState<(() => void) | null>(null);

   const [selectedShipping, setSelectedShipping] = useState<any>(null);
   const [appliedCoupon, setAppliedCoupon] = useState<any>(null);

   const [isShareModalOpen, setIsShareModalOpen] = useState(false);
   const [shareData, setShareData] = useState({ url: '', title: '' });

   const getProductImages = (product: any) => {
      const imgs = []
      if (product.image_url) imgs.push(product.image_url);
      let gallery = product.images;
      if (typeof gallery === 'string') {
         try { gallery = JSON.parse(gallery); } catch (e) { gallery = []; }
      }
      if (gallery && Array.isArray(gallery)) {
         gallery.forEach((img: string) => imgs.push(img));
      }
      return imgs.length > 0 ? imgs : null;
   };

   const getCartItemId = (productId: string, variations?: { [key: string]: string }) => {
      if (productId.includes('--')) return productId;
      if (!variations || Object.keys(variations).length === 0) return productId;
      const variationKey = Object.entries(variations)
         .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
         .map(([key, value]) => `${key.toLowerCase()}:${value.toLowerCase()}`)
         .join('-');
      return `${productId}--${variationKey}`;
   };

   const getProductIdFromCartItemId = (cartItemId: string) => cartItemId.split('--')[0];

   const safeFloat = (val: any) => {
      if (val === null || val === undefined || val === '') return 0;
      const str = String(val).replace(',', '.');
      return parseFloat(str) || 0;
   };

   const getProductPrice = (product: any, variations?: { [key: string]: string }) => {
      const price = safeFloat(product.price);
      const promo = safeFloat(product.promotional_price);
      let finalPrice = (promo > 0 && promo < price) ? promo : price;
      const variationsToUse = variations || selectedVariations[product.id];
      if (!product || !variationsToUse) return finalPrice;
      const pVars = product.variations ? (typeof product.variations === 'string' ? JSON.parse(product.variations) : product.variations) : [];
      if (!Array.isArray(pVars)) return finalPrice;
      pVars.forEach((v: any) => {
         const selectedOption = variationsToUse[v.name];
         if (selectedOption && v.prices && v.prices[selectedOption]) {
            const config = v.prices[selectedOption];
            const priceStr = typeof config === 'string' ? config : config.price;
            const specificPrice = safeFloat(priceStr);
            if (specificPrice > 0) finalPrice = specificPrice;
         }
      });
      return finalPrice;
   };

   const cartItems = React.useMemo(() => {
      return Object.entries(cart).map(([cartItemId, cartData]) => {
         const qty = typeof cartData === 'number' ? cartData : (cartData as any).qty;
         const savedVariations = typeof cartData === 'object' ? (cartData as any).variations : undefined;
         const productId = getProductIdFromCartItemId(cartItemId);
         const product = products.find(p => p.id === productId);
         if (!product) return null;
         const variationsToUse = savedVariations || selectedVariations[productId];
         const unitPrice = getProductPrice(product, variationsToUse);
         const rawPrice = safeFloat(product.price);
         const allImgs = getProductImages(product);
         let variationImage = product.image_url;
         if (allImgs && variationsToUse) {
            let foundIndex = -1;
            const pVars = product.variations ? (typeof product.variations === 'string' ? JSON.parse(product.variations) : product.variations) : [];
            if (Array.isArray(pVars)) {
               pVars.forEach((v: any) => {
                  const opt = variationsToUse[v.name];
                  if (opt && v.prices && typeof v.prices[opt] === 'object') {
                     const imgIdx = v.prices[opt].imageIndex;
                     if (imgIdx === 'cover') foundIndex = 0;
                     else if (imgIdx !== undefined && imgIdx !== '') foundIndex = (product.image_url ? 1 : 0) + Number(imgIdx);
                  }
               });
            }
            if (foundIndex === -1) {
               const searchTerms = Object.values(variationsToUse).map(v => String(v).toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, ""));
               foundIndex = allImgs.findIndex(img => searchTerms.some(term => img.toLowerCase().includes(term)));
            }
            if (foundIndex !== -1 && allImgs[foundIndex]) variationImage = allImgs[foundIndex];
         }
         let finalDisplayPrice = rawPrice;
         let finalPromoPrice: number | null = unitPrice;
         if (unitPrice < rawPrice) {
            finalDisplayPrice = rawPrice;
            finalPromoPrice = unitPrice;
         } else {
            finalDisplayPrice = unitPrice;
            finalPromoPrice = null;
         }
         return {
            ...product,
            id: cartItemId,
            image_url: variationImage,
            quantity: qty,
            price: finalDisplayPrice,
            promotional_price: finalPromoPrice,
            selected_options: variationsToUse
         };
      }).filter(Boolean) as any[];
   }, [cart, products, selectedVariations]);

   useEffect(() => {
      const loadData = async () => {
         let effectiveTenantId = tenantId;

         // Se não houver tenantId na URL, tentamos identificar pelo domínio (Custom Domain)
         if (!effectiveTenantId) {
            try {
               const identifyRes = await axios.get('/api/store/identify');
               if (identifyRes.data.custom_domain && identifyRes.data.tenant) {
                  effectiveTenantId = identifyRes.data.tenant.id;
               } else {
                  setLoading(false);
                  return;
               }
            } catch (err) {
               console.error("Erro na identificação do domínio:", err);
               setLoading(false);
               return;
            }
         }

         try {
            const [storeData, productsData, categoriesData] = await Promise.all([
               getStoreInfo(effectiveTenantId),
               getProducts(effectiveTenantId),
               getCategories(effectiveTenantId)
            ]);
            setStore(storeData);
            setProducts(productsData);
            setCategories(categoriesData);
         } catch (error) {
            console.error("Erro ao carregar dados:", error);
         } finally {
            setLoading(false);
         }
      };
      loadData();
   }, [tenantId]);

   useEffect(() => {
      if (!loading && products.length > 0) {
         const deepLinkProduct = searchParams.get('p');
         if (deepLinkProduct) {
            const targetProduct = products.find(prod => prod.id === deepLinkProduct);
            if (targetProduct) setSelectedProduct(targetProduct);
         }
      }
   }, [loading, products, searchParams]);

   useEffect(() => {
      if (selectedProduct) {
         setCurrentImageIndex(0);
         const pVars = selectedProduct.variations ? (typeof selectedProduct.variations === 'string' ? JSON.parse(selectedProduct.variations) : selectedProduct.variations) : [];
         if (Array.isArray(pVars)) {
            const updates: any = {};
            pVars.forEach((v: any) => {
               if (v.options && v.options.length === 1) updates[v.name] = v.options[0];
            });
            if (Object.keys(updates).length > 0) {
               setSelectedVariations(prev => ({ ...prev, [selectedProduct.id]: { ...(prev[selectedProduct.id] || {}), ...updates } }));
            }
         }
      }
   }, [selectedProduct]);

   useEffect(() => {
      const recoveryOrderId = searchParams.get('order_id');
      const autoCheckout = searchParams.get('checkout') === 'true';
      const restoreCart = async () => {
         if (recoveryOrderId && products.length > 0) {
            try {
               const res = await axios.get(`https://zapshop.app/api/store/order-items/${recoveryOrderId}`);
               const items = res.data;
               const recoveredCart: { [key: string]: { qty: number, variations?: any } } = {};
               items.forEach((item: any) => {
                  const variations = item.selected_variations || {};
                  const cartItemId = getCartItemId(item.product_id, variations);
                  recoveredCart[cartItemId] = { qty: item.quantity, variations: variations };
               });
               setCart(recoveredCart);
               if (autoCheckout) setIsCheckoutOpen(true);
            } catch (err) {
               console.error("Falha ao restaurar sacola:", err);
            }
         }
      };
      restoreCart();
   }, [loading, products, searchParams]);

   const filteredProducts = products.filter(p => {
      const matchesCategory = selectedCategory === 'all' || p.category_id === selectedCategory;
      const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
      const price = parseFloat(p.promotional_price || p.price);
      const matchesPrice = price >= priceRange[0] && price <= priceRange[1];
      return matchesCategory && matchesSearch && matchesPrice;
   });

   const updateQty = (id: string, delta: number, e?: React.MouseEvent, variations?: { [key: string]: string }) => {
      if (e) e.stopPropagation();
      const cartItemId = getCartItemId(id, variations || selectedVariations[id]);
      setCart(prev => {
         const currentData = prev[cartItemId];
         const currentQty = typeof currentData === 'number' ? currentData : (currentData?.qty || 0);
         const newQty = currentQty + delta;
         if (newQty <= 0) {
            const copy = { ...prev };
            delete copy[cartItemId];
            return copy;
         }
         if (delta > 0 && currentQty === 0) showToast("Item adicionado à sacola!", "success");
         return { ...prev, [cartItemId]: { qty: newQty, variations: variations || selectedVariations[id] || (typeof currentData === 'object' ? currentData.variations : undefined) } };
      });
   };

   const handleProceedToCheckout = (shippingData?: any) => {
      if (shippingData) setSelectedShipping(shippingData);
      setIsCartOpen(false);
      setIsCheckoutOpen(true);
   };

   const ProductSkeleton = () => (
      <div className="bg-white rounded-3xl p-4 shadow-sm border border-gray-50 flex flex-col h-full animate-pulse">
         <div className="aspect-square rounded-2xl bg-gray-100 mb-4"></div>
         <div className="h-4 bg-gray-100 rounded w-3/4 mb-2"></div>
         <div className="h-4 bg-gray-100 rounded w-1/2 mb-4"></div>
         <div className="mt-auto h-6 bg-gray-100 rounded w-1/4"></div>
      </div>
   );

   if (!store && loading) return (
      <div className="min-h-screen bg-surface-50">
         <div className="h-20 bg-white border-b border-gray-100 flex items-center px-8">
            <div className="w-32 h-8 bg-gray-100 rounded animate-pulse"></div>
         </div>
         <div className="max-w-7xl mx-auto p-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
               {[1, 2, 3, 4, 5, 6, 7, 8].map(i => <ProductSkeleton key={i} />)}
            </div>
         </div>
      </div>
   );

   if (!store) return <div className="min-h-screen flex items-center justify-center">Loja não encontrada.</div>;

   const primaryColor = store.config_json?.primary_color || '#2563eb';
   const cartTotal = Object.entries(cart).reduce((total, [cartItemId, cartData]) => {
      const productId = getProductIdFromCartItemId(cartItemId);
      const p = products.find(p => p.id === productId);
      if (!p) return total;
      const qty = typeof cartData === 'number' ? cartData : (cartData as any).qty;
      const savedVariations = typeof cartData === 'object' ? (cartData as any).variations : undefined;
      return total + (getProductPrice(p, savedVariations) * qty);
   }, 0);

   return (
      <div className="min-h-screen bg-surface-100 font-sans text-surface-900">
         <header className="bg-white sticky top-0 z-30 shadow-sm border-b border-surface-200">
            <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-between gap-4">
               <div className="flex items-center gap-3">
                  <div className="w-14 h-14 md:w-20 md:h-20 rounded-lg bg-surface-100 overflow-hidden border border-surface-200">
                     {store.config_json?.logo_url ? <img src={`/api${store.config_json.logo_url}`} className="w-full h-full object-contain" /> : <Store size={20} className="m-auto mt-2 text-surface-400" />}
                  </div>
                  <div>
                     <h1 className="font-bold text-surface-900 leading-tight">{store.name}</h1>
                     <div className="flex items-center gap-1"><div className="w-1.5 h-1.5 rounded-full bg-green-500 animate-pulse"></div><span className="text-[10px] uppercase font-bold text-surface-500">Online</span></div>
                  </div>
               </div>
               <div className="hidden md:flex flex-1 max-w-md mx-auto relative focus-within:scale-[1.02] transition-transform">
                  <input placeholder="O que você procura hoje?" className="w-full pl-10 pr-4 py-2.5 bg-surface-100 border-none rounded-2xl text-sm focus:ring-4 focus:ring-brand-500/10 focus:bg-white transition-all outline-none shadow-inner" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-400" size={16} />
               </div>
               <div className="flex items-center gap-2">
                  <button onClick={() => {
                     const shareUrl = tenantId
                        ? `${window.location.origin}/store/${store?.slug || tenantId}`
                        : window.location.origin;
                     setShareData({ url: shareUrl, title: store?.name || 'ZapShop' });
                     setIsShareModalOpen(true);
                  }} className="p-2.5 text-surface-600 hover:bg-surface-100 rounded-xl transition-colors">
                     <Share2 size={20} />
                  </button>
                  <button onClick={() => setShowFiltersMobile(!showFiltersMobile)} className="md:hidden p-2.5 text-surface-600 hover:bg-surface-100 rounded-xl"><Filter size={20} /></button>
                  <button onClick={() => setIsCartOpen(true)} className="relative p-2.5 text-surface-600 hover:bg-surface-100 rounded-xl group">
                     <ShoppingBag size={24} className="group-hover:text-brand-600 transition" />
                     {cartItems.length > 0 && <span className="absolute top-1 right-1 bg-red-500 text-white text-[10px] font-bold h-5 w-5 flex items-center justify-center rounded-full border-2 border-white shadow-sm">{cartItems.reduce((a, b) => a + b.quantity, 0)}</span>}
                  </button>
               </div>
            </div>
            <div className="md:hidden px-4 pb-3">
               <div className="relative">
                  <input placeholder="Buscar produtos..." className="w-full pl-10 pr-4 py-2.5 bg-surface-100 rounded-xl text-sm outline-none" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} />
                  <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-surface-400" size={18} />
               </div>
            </div>
         </header>

         <div className="max-w-7xl mx-auto p-4 md:p-8 flex flex-col md:flex-row gap-8">
            <aside className="hidden md:block w-64 shrink-0 p-6 bg-white rounded-3xl shadow-sm border border-surface-200 space-y-8 sticky top-24 h-fit">
               <div>
                  <h3 className="text-xs font-bold text-surface-400 uppercase tracking-widest mb-4">Categorias</h3>
                  <div className="flex flex-col gap-1">
                     <button onClick={() => setSelectedCategory('all')} className={`text-left px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${selectedCategory === 'all' ? 'bg-white shadow-sm font-bold' : 'text-surface-500 hover:bg-surface-50'}`} style={selectedCategory === 'all' ? { color: primaryColor } : {}}>Tudo</button>
                     {categories.map(cat => (
                        <button key={cat.id} onClick={() => setSelectedCategory(cat.id)} className={`text-left px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${selectedCategory === cat.id ? 'bg-white shadow-sm font-bold' : 'text-surface-500 hover:bg-surface-50'}`} style={selectedCategory === cat.id ? { color: primaryColor } : {}}>{cat.name}</button>
                     ))}
                  </div>
               </div>
               <div>
                  <h3 className="text-xs font-bold text-surface-400 uppercase tracking-widest mb-4">Preço</h3>
                  <div className="bg-white p-4 rounded-2xl shadow-sm border border-surface-50">
                     <div className="flex justify-between items-center mb-4">
                        <span className="text-xs font-bold text-surface-600">Máximo</span>
                        <span className="text-sm font-bold" style={{ color: primaryColor }}>R$ {priceRange[1]}</span>
                     </div>
                     <input type="range" min="0" max="1000" step="10" value={priceRange[1]} className="w-full h-1.5 bg-surface-100 rounded-lg appearance-none outline-none mb-2" onChange={(e) => setPriceRange([0, parseInt(e.target.value)])} style={{ accentColor: primaryColor }} />
                     {priceRange[1] < 1000 && <button onClick={() => setPriceRange([0, 1000])} className="w-full mt-2 text-xs text-center text-surface-400 hover:text-brand-600 underline">Limpar Filtro</button>}
                  </div>
               </div>
            </aside>

            <main className="flex-1">
               <div className="flex md:hidden gap-3 overflow-x-auto pb-4 mb-6 scroll-smooth snap-x relative [&::-webkit-scrollbar]:hidden" style={{ scrollbarWidth: 'none' }}>
                  <button onClick={() => setSelectedCategory('all')} className={`zapshop-category-chip snap-start ${selectedCategory === 'all' ? 'active' : ''}`} style={selectedCategory === 'all' ? { backgroundColor: primaryColor, borderColor: primaryColor } : {}}>Tudo</button>
                  {categories.map(cat => (
                     <button key={cat.id} onClick={() => setSelectedCategory(cat.id)} className={`zapshop-category-chip snap-start ${selectedCategory === cat.id ? 'active' : ''}`} style={selectedCategory === cat.id ? { backgroundColor: primaryColor, borderColor: primaryColor } : {}}>{cat.name}</button>
                  ))}
               </div>

               {filteredProducts.length === 0 ? (
                  <div className="text-center py-20 bg-white rounded-3xl border border-dashed border-surface-200">
                     <Search size={48} className="mx-auto text-surface-300 mb-4" />
                     <p className="text-surface-500">Nenhum produto encontrado.</p>
                     <button onClick={() => { setSearchTerm(''); setSelectedCategory('all'); setPriceRange([0, 10000]); }} className="mt-4 text-brand-600 font-bold hover:underline">Limpar Filtros</button>
                  </div>
               ) : (
                  <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6">
                     {filteredProducts.map(product => {
                        const isOutOfStock = product.stock <= 0;
                        const canBackorder = product.allow_backorder === true;
                        const isSoldOut = isOutOfStock && !canBackorder;
                        return (
                           <div key={product.id} onClick={() => !isSoldOut && setSelectedProduct(product)} className={`card-base group relative flex flex-col h-full hover:-translate-y-2 hover:shadow-[0_8px_30px_rgba(0,0,0,0.12)] transition-all duration-300 animate-fade-in ${isSoldOut ? 'opacity-60 cursor-not-allowed grayscale-[0.5]' : 'cursor-pointer'}`}>
                              <div className="aspect-square rounded-[14px] bg-white relative overflow-hidden mb-4 border border-surface-100 flex items-center justify-center">
                                 {product.image_url ? <img src={product.image_url.startsWith('/uploads') ? `/api${product.image_url}` : product.image_url} className="max-w-full max-h-full object-contain group-hover:scale-110 transition duration-700" /> : <Store className="m-auto text-surface-300 w-1/3 h-1/3" />}
                                 {product.promotional_price && !isOutOfStock && <span className="absolute top-2 left-2 bg-red-50/80 backdrop-blur text-red-600 border border-red-100 text-[10px] font-bold px-2.5 py-1 rounded-full z-10">-{Math.round((1 - parseFloat(product.promotional_price) / parseFloat(product.price)) * 100)}%</span>}
                                 {isSoldOut ? <span className="absolute top-2 right-2 bg-red-50 text-red-600 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border border-red-100/50 z-10">Esgotado</span> : isOutOfStock && canBackorder && <span className="absolute top-2 right-2 bg-blue-50 text-blue-600 text-[10px] font-bold px-2.5 py-1 rounded-full z-10 border border-blue-100/50">Encomenda</span>}
                                 {!isSoldOut && (
                                    <button onClick={(e) => { e.stopPropagation(); setSelectedProduct(product); }} className="absolute bottom-2 right-2 bg-brand-600 text-white p-2.5 rounded-xl shadow-lg opacity-0 group-hover:opacity-100 translate-y-2 group-hover:translate-y-0 transition-all z-20" style={{ backgroundColor: primaryColor }}><Plus size={20} /></button>
                                 )}
                              </div>
                              <div className="flex flex-col flex-1 px-1">
                                 <h3 className="font-normal text-surface-600 text-sm leading-tight line-clamp-2 mb-1 min-h-[2.5em]">{product.name}</h3>
                                 <div className="mt-auto">
                                    {product.promotional_price ? (
                                       <div className="flex flex-col">
                                          <span className="text-xs text-surface-400 line-through">R$ {Number(product.price).toFixed(2)}</span>
                                          <span className="text-xl font-extrabold text-surface-900">R$ {Number(product.promotional_price).toFixed(2)}</span>
                                       </div>
                                    ) : <span className="text-xl font-extrabold text-surface-900 mt-2 block">R$ {Number(product.price).toFixed(2)}</span>}
                                 </div>
                              </div>
                           </div>
                        );
                     })}
                  </div>
               )}
            </main>
         </div>

         {showFiltersMobile && (
            <div className="fixed inset-0 z-[60] flex items-end md:hidden">
               <div className="absolute inset-0 bg-black/60 backdrop-blur-sm animate-fade-in" onClick={() => setShowFiltersMobile(false)}></div>
               <div className="bg-white w-full rounded-t-[32px] p-8 pb-10 shadow-2xl z-10 animate-slide-up relative">
                  <div className="w-12 h-1.5 bg-surface-200 rounded-full mx-auto mb-8"></div>
                  <div className="flex items-center justify-between mb-8">
                     <h3 className="text-xl font-bold text-surface-900">Filtros</h3>
                     <button onClick={() => setShowFiltersMobile(false)} className="p-2 bg-surface-50 rounded-full text-surface-400 hover:text-surface-900 transition-colors"><X size={20} /></button>
                  </div>
                  <div className="space-y-10">
                     <div>
                        <div className="flex items-center justify-between mb-6">
                           <span className="text-sm font-bold text-surface-400 uppercase tracking-widest">Faixa de Preço</span>
                           <span className="text-lg font-bold" style={{ color: primaryColor }}>Até R$ {priceRange[1]}</span>
                        </div>
                        <div className="px-2">
                           <input type="range" min="0" max="1000" step="10" value={priceRange[1]} className="w-full h-2 bg-surface-100 rounded-lg appearance-none outline-none" onChange={(e) => setPriceRange([0, parseInt(e.target.value)])} style={{ accentColor: primaryColor }} />
                        </div>
                     </div>
                     <button onClick={() => setShowFiltersMobile(false)} className="w-full py-4 rounded-2xl font-bold text-white shadow-lg active:scale-[0.96] transition-all" style={{ backgroundColor: primaryColor }}>Ver {filteredProducts.length} Produtos</button>
                  </div>
               </div>
            </div>
         )}

         <Footer storeName={store?.name} phone={store?.phone} email={store?.config_json?.email} address={store?.config_json?.address} cnpj={store?.config_json?.cnpj} primaryColor={primaryColor} instagramUrl={store?.config_json?.instagram_url} description={store?.config_json?.description} />

         <CartBar items={cartItems} onClick={() => setIsCartOpen(true)} primaryColor={primaryColor} />

         {store.phone && (
            <a
               href={`https://wa.me/${store.phone.replace(/\D/g, '').length <= 11 ? '55' + store.phone.replace(/\D/g, '') : store.phone.replace(/\D/g, '')}`}
               target="_blank"
               rel="noreferrer"
               className="fixed bottom-24 right-5 z-[45] bg-[#25D366] text-white p-3 md:p-3.5 rounded-2xl shadow-2xl hover:scale-110 active:scale-95 transition-all group border-2 md:border-4 border-white"
            >
               <MessageCircle className="fill-current w-5 h-5 md:w-6 md:h-6" />
               <span className="absolute right-full mr-3 bg-gray-900 text-white text-[10px] font-bold px-3 py-1.5 rounded-lg whitespace-nowrap opacity-0 md:group-hover:opacity-100 transition-opacity uppercase tracking-widest shadow-lg border border-white/10 hidden md:block">Fale Conosco</span>
            </a>
         )}

         <CartModal
            isOpen={isCartOpen}
            onClose={() => setIsCartOpen(false)}
            items={cartItems}
            tenantId={store?.id || tenantId || ''}
            onUpdateQuantity={(id, delta) => updateQty(id, delta)}
            onCheckout={handleProceedToCheckout}
            isSubmitting={false}
            primaryColor={primaryColor}
            showToast={showToast}
            deliveryRadius={store?.delivery_radius_km}
            originZipCode={store?.origin_zip_code}
            deliveryFixedPrice={store?.delivery_fixed_price}
            deliveryFeeType={store?.delivery_fee_type}
         />

         {selectedProduct && (
            <div className="fixed inset-0 z-50 flex items-center justify-center p-0 md:p-10 pointer-events-none overflow-hidden">
               <div className="absolute inset-0 bg-black/60 backdrop-blur-sm pointer-events-auto animate-fade-in" onClick={() => setSelectedProduct(null)} />
               <div className="bg-white w-full h-full md:h-fit md:max-w-6xl md:rounded-[40px] shadow-2xl flex flex-col pointer-events-auto animate-slide-up relative lg:overflow-hidden">
                  <button onClick={() => setSelectedProduct(null)} className="absolute top-4 right-4 md:top-8 md:right-8 z-50 p-2.5 bg-white/80 backdrop-blur shadow-lg rounded-full text-surface-900 hover:scale-110 transition-all"><X size={20} /></button>
                  <div className="flex flex-col md:grid md:grid-cols-2 h-full overflow-y-auto">
                     <div className="relative h-[40vh] md:h-auto bg-white group border-b border-surface-50 md:border-b-0">
                        {(() => {
                           const imgs = getProductImages(selectedProduct);
                           return imgs ? (
                              <>
                                 <img src={imgs[currentImageIndex].startsWith('/uploads') ? `/api${imgs[currentImageIndex]}` : imgs[currentImageIndex]} className="w-full h-full object-contain transition-transform duration-700 hover:scale-105 cursor-zoom-in" />
                                 {imgs.length > 1 && (
                                    <>
                                       <button onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(prev => prev > 0 ? prev - 1 : imgs.length - 1) }} className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 p-3 rounded-full shadow-xl opacity-0 group-hover:opacity-100 transition-all"><ChevronLeft size={24} /></button>
                                       <button onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(prev => prev < imgs.length - 1 ? prev + 1 : 0) }} className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 p-3 rounded-full shadow-xl opacity-0 group-hover:opacity-100 transition-all"><ChevronRight size={24} /></button>
                                       <div className="absolute bottom-8 left-0 right-0 flex justify-center gap-3">
                                          {imgs.map((_, i) => <button key={i} onClick={(e) => { e.stopPropagation(); setCurrentImageIndex(i) }} className={`h-1.5 rounded-full transition-all ${i === currentImageIndex ? 'bg-surface-900 w-8' : 'bg-surface-300 w-4'}`} />)}
                                       </div>
                                    </>
                                 )}
                              </>
                           ) : <div className="w-full h-full flex items-center justify-center bg-surface-100"><Store size={64} className="text-surface-300" /></div>;
                        })()}
                     </div>
                     <div className="flex flex-col p-4 md:p-12 lg:p-16 bg-white overflow-y-auto">
                        <div className="flex items-center gap-2 mb-4">
                           <span className="px-3 py-1 bg-surface-100 text-surface-600 rounded-full text-[10px] font-bold uppercase tracking-wider">{selectedProduct.category_name || 'Produto'}</span>
                           {selectedProduct.promotional_price && <span className="px-3 py-1 bg-red-50 text-red-600 rounded-full text-[10px] font-bold uppercase tracking-wider">Oferta</span>}
                           {selectedProduct.stock <= 0 && selectedProduct.allow_backorder !== true ? (
                              <span className="px-3 py-1 bg-red-50 text-red-600 rounded-full text-[10px] font-bold uppercase tracking-wider">Esgotado</span>
                           ) : selectedProduct.stock <= 0 && selectedProduct.allow_backorder === true && (
                              <span className="px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-[10px] font-bold uppercase tracking-wider">Encomenda</span>
                           )}
                        </div>
                        <h2 className="text-xl md:text-4xl font-extrabold text-surface-900 mb-1 leading-tight">{selectedProduct.name}</h2>
                        <div className="flex items-center gap-3 mb-4">
                           {selectedProduct.promotional_price ? (
                              <>
                                 <span className="text-3xl font-black text-surface-900">R$ {Number(selectedProduct.promotional_price).toFixed(2)}</span>
                                 <span className="text-lg text-surface-400 line-through">R$ {Number(selectedProduct.price).toFixed(2)}</span>
                              </>
                           ) : <span className="text-3xl font-black text-surface-900">R$ {Number(selectedProduct.price).toFixed(2)}</span>}
                        </div>
                        <div className="prose prose-sm text-surface-600 mb-4 leading-relaxed uppercase tracking-wide">
                           {selectedProduct.description ? selectedProduct.description.split('\n').map((line: string, i: number) => <p key={i} className="mb-2">{line}</p>) : <p>Sem descrição disponível.</p>}
                        </div>
                        <div className="space-y-4 mb-6">
                           {(typeof selectedProduct.variations === 'string' ? JSON.parse(selectedProduct.variations || '[]') : selectedProduct.variations || []).map((v: any) => (
                              <div key={v.name}>
                                 <label className="block text-[11px] font-black text-surface-400 uppercase tracking-[0.2em] mb-4">{v.name}</label>
                                 <div className="flex flex-wrap gap-3">
                                    {(v.options || v.values || []).map((val: string) => {
                                       const isSelected = selectedVariations[selectedProduct.id]?.[v.name] === val;
                                       return (
                                          <button key={val} onClick={() => setSelectedVariations(prev => ({ ...prev, [selectedProduct.id]: { ...(prev[selectedProduct.id] || {}), [v.name]: val } }))} className={`px-6 py-3 rounded-xl text-sm font-bold transition-all border-2 active:scale-95 ${isSelected ? 'border-surface-900 bg-surface-900 text-white shadow-md' : 'border-surface-100 bg-surface-50 text-surface-600 hover:border-surface-200'}`} style={isSelected ? { backgroundColor: primaryColor, borderColor: primaryColor } : {}}>{val}</button>
                                       );
                                    })}
                                 </div>
                              </div>
                           ))}
                        </div>

                        {/* Compartilhar - Compacto */}
                        <div className="mb-6">
                           <div className="flex items-center gap-2 mb-3">
                              <Share2 size={12} className="text-surface-400" />
                              <span className="text-[9px] font-black text-surface-400 uppercase tracking-widest">Compartilhar Produto</span>
                           </div>
                           <div className="flex gap-2">
                              <button
                                 onClick={() => {
                                    const baseUrl = tenantId
                                       ? `${window.location.origin}/store/${store?.slug || tenantId}`
                                       : window.location.origin;
                                    const productUrl = `${baseUrl}?p=${selectedProduct.id}`;
                                    const message = encodeURIComponent(`Confira ${selectedProduct.name} no ZapShop! 🚀\n\n${productUrl}`);
                                    window.open(`https://wa.me/?text=${message}`, '_blank');
                                 }}
                                 className="flex items-center gap-2 px-4 py-2 bg-emerald-50 text-emerald-700 rounded-xl transition-all font-bold text-[10px] border border-emerald-100/50 active:scale-95"
                              >
                                 <MessageCircle size={14} fill="currentColor" />
                                 WhatsApp
                              </button>
                              <button
                                 onClick={() => {
                                    const baseUrl = tenantId
                                       ? `${window.location.origin}/store/${store?.slug || tenantId}`
                                       : window.location.origin;
                                    const productUrl = `${baseUrl}?p=${selectedProduct.id}`;
                                    navigator.clipboard.writeText(productUrl);
                                    showToast('Link copiado!', 'success');
                                 }}
                                 className="flex items-center gap-2 px-4 py-2 bg-surface-50 text-surface-600 rounded-xl transition-all font-bold text-[10px] border border-surface-200 active:scale-95"
                              >
                                 <Share2 size={14} />
                                 Copiar Link
                              </button>
                           </div>
                        </div>
                        <div className="mt-8 pt-4 border-t border-surface-100 flex flex-col md:flex-row gap-3">
                           <button onClick={() => setSelectedProduct(null)} className="flex-1 py-3 border border-surface-200 text-surface-600 font-bold rounded-xl hover:bg-surface-50 uppercase text-[10px] tracking-widest active:scale-[0.96] transition-all">Voltar</button>
                           {(() => {
                              const productVars = typeof selectedProduct.variations === 'string' ? JSON.parse(selectedProduct.variations || '[]') : selectedProduct.variations || [];
                              const isSelectionComplete = !productVars.length || (selectedVariations[selectedProduct.id] ? Object.keys(selectedVariations[selectedProduct.id]).length === productVars.length : false);
                              const cartData = cart[selectedProduct.id];
                              const currentQty = typeof cartData === 'number' ? cartData : (cartData?.qty || 0);
                              const isOutOfStock = selectedProduct.stock <= 0;
                              const canBackorder = selectedProduct.allow_backorder === true;
                              const isSoldOut = isOutOfStock && !canBackorder;

                              return (
                                 <button
                                    disabled={!isSelectionComplete || isSoldOut}
                                    onClick={() => {
                                       const isBackorderItem = selectedProduct.allow_backorder === true && selectedProduct.stock <= 0;
                                       if (isBackorderItem) {
                                          setPendingAction(() => () => { if (currentQty === 0) updateQty(selectedProduct.id, 1, undefined, selectedVariations[selectedProduct.id]); setSelectedProduct(null); setIsCartOpen(true); });
                                          setShowBackorderModal(true);
                                          return;
                                       }
                                       if (currentQty === 0) updateQty(selectedProduct.id, 1, undefined, selectedVariations[selectedProduct.id]);
                                       setSelectedProduct(null);
                                       setIsCartOpen(true);
                                    }}
                                    className="flex-[2] py-3 rounded-xl text-white font-bold shadow-md active:scale-[0.96] transition-all uppercase text-[10px] tracking-widest disabled:grayscale disabled:opacity-50"
                                    style={{ backgroundColor: isSoldOut ? '#ef4444' : (isSelectionComplete ? primaryColor : '#d1d5db') }}
                                 >
                                    {isSoldOut ? 'Esgotado' : (isSelectionComplete ? `Adicionar ao carrinho` : 'Selecione Opções')}
                                 </button>
                              );
                           })()}
                        </div>
                     </div>
                  </div>
               </div>
            </div>
         )}
         <ShareModal isOpen={isShareModalOpen} onClose={() => setIsShareModalOpen(false)} url={shareData.url} title={shareData.title} showToast={showToast} />
         <CheckoutModal isOpen={isCheckoutOpen} onClose={() => setIsCheckoutOpen(false)} items={cartItems} total={cartTotal} shippingCost={selectedShipping?.price || 0} selectedShipping={selectedShipping} appliedCoupon={appliedCoupon} tenantId={store?.id || tenantId || ''} onClearCart={() => setCart({})} storePhone={store?.phone} showToast={showToast} storeConfig={store?.config_json} />
         <BackorderModal isOpen={showBackorderModal} onClose={() => setShowBackorderModal(false)} onConfirm={() => { if (pendingAction) { pendingAction(); setShowBackorderModal(false); } }} primaryColor={primaryColor} />
      </div>
   );
};

export default PublicStore;
